import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../../supabase";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { CPInput } from "../CPInput";
import {
  ARCHIVE_DEFAULT_BLOCKED_MESSAGE,
  archiveWorkType,
  createWorkType,
  listWorkTypes,
  refreshWorkTypeSessionCache,
  reorderWorkTypeDisplayOrders,
  restoreWorkType,
  setDefaultWorkType,
  updateWorkType,
} from "../../helpers/catalogs/workTypeCatalog";
import { sortCatalogItems } from "../../helpers/catalogs/catalogHelpers";
import { usePermissions } from "../../hooks/usePermissions";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { DeskEmpty, DeskPill, DeskTable } from "../desktop/DeskTable";
import { AdmMoveButtons } from "./desktop/AdmKit";
import "./AdministrationHub.css";

function DefaultBadge() {
  return (
    <span className="admin-status-badge admin-status-badge--accepted">
      Default
    </span>
  );
}

function WorkTypeRow({
  item,
  items,
  onRename,
  onArchive,
  onRestore,
  onMove,
  onSetDefault,
}) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(item.name);

  const canMoveUp =
    reorderWorkTypeDisplayOrders(items, item.work_type_id, -1) != null;
  const canMoveDown =
    reorderWorkTypeDisplayOrders(items, item.work_type_id, 1) != null;

  return (
    <div className="work-type-row">
      <div className="work-type-row-main">
        {editing ? (
          <CPInput
            className="cp-input-group--compact"
            aria-label={`Rename ${item.name}`}
            value={draftName}
            onChange={setDraftName}
          />
        ) : (
          <strong className="admin-data-primary">{item.name}</strong>
        )}
        {item.is_default ? <DefaultBadge /> : null}
        {!item.is_active ? (
          <span className="admin-status-badge admin-status-badge--pending">
            Archived
          </span>
        ) : null}
      </div>
      <div className="work-type-row-actions">
        {editing ? (
          <>
            <button
              type="button"
              className="kpi-back-btn"
              onClick={async () => {
                const saved = await onRename(item, draftName);
                if (saved) {
                  setEditing(false);
                }
              }}
            >
              Save
            </button>
            <button
              type="button"
              className="kpi-back-btn"
              onClick={() => {
                setDraftName(item.name);
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="kpi-back-btn"
              disabled={!canMoveUp}
              onClick={() => onMove(item, -1)}
            >
              Up
            </button>
            <button
              type="button"
              className="kpi-back-btn"
              disabled={!canMoveDown}
              onClick={() => onMove(item, 1)}
            >
              Down
            </button>
            <button
              type="button"
              className="kpi-back-btn"
              onClick={() => {
                setDraftName(item.name);
                setEditing(true);
              }}
            >
              Rename
            </button>
            {item.is_active && !item.is_default ? (
              <button
                type="button"
                className="kpi-back-btn"
                onClick={() => onSetDefault(item)}
              >
                Set as default
              </button>
            ) : null}
            {item.is_active ? (
              <button
                type="button"
                className="kpi-back-btn"
                aria-disabled={item.is_default ? "true" : undefined}
                title={item.is_default ? ARCHIVE_DEFAULT_BLOCKED_MESSAGE : undefined}
                onClick={() => onArchive(item)}
              >
                Archive
              </button>
            ) : (
              <button
                type="button"
                className="kpi-back-btn"
                onClick={() => onRestore(item)}
              >
                Restore
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function AdminWorkTypesScreen({ onBack }) {
  const { actor } = usePermissions();
  const isDesktop = useIsDesktopViewport();
  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const loadItems = useCallback(async ({ silent = false } = {}) => {
    if (!actor) {
      setItems([]);
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
    }
    setError("");
    try {
      const rows = await listWorkTypes(supabase, {
        activeOnly: false,
        actor,
      });
      setItems(sortCatalogItems(rows));
    } catch (err) {
      console.error("LOAD WORK TYPES ERROR:", err);
      setError("Unable to load work types.");
    } finally {
      setLoading(false);
    }
  }, [actor]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const markSaved = () => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2000);
  };

  const afterChange = async () => {
    await refreshWorkTypeSessionCache(supabase, actor);
    await loadItems({ silent: true });
    markSaved();
  };

  const handleAdd = async () => {
    setError("");
    try {
      const nextOrder =
        items.reduce(
          (max, item) => Math.max(max, Number(item.display_order || 0)),
          0,
        ) + 1;
      await createWorkType(
        { name: newName, display_order: nextOrder },
        supabase,
        actor,
      );
      setNewName("");
      await afterChange();
    } catch (err) {
      console.error("ADD WORK TYPE ERROR:", err);
      setError(err.message || "Failed to add work type.");
    }
  };

  const handleRename = async (item, name) => {
    setError("");
    try {
      await updateWorkType(item.work_type_id, { name }, supabase, actor);
      await afterChange();
      return true;
    } catch (err) {
      console.error("RENAME WORK TYPE ERROR:", err);
      setError(err.message || "Failed to rename work type.");
      return false;
    }
  };

  const handleArchive = async (item) => {
    setError("");
    if (item?.is_default) {
      setError(ARCHIVE_DEFAULT_BLOCKED_MESSAGE);
      return;
    }
    try {
      await archiveWorkType(item.work_type_id, supabase, actor);
      await afterChange();
    } catch (err) {
      console.error("ARCHIVE WORK TYPE ERROR:", err);
      setError(err.message || "Failed to archive work type.");
    }
  };

  const handleSetDefault = async (item) => {
    setError("");
    try {
      await setDefaultWorkType(item.work_type_id, supabase, actor);
      await afterChange();
    } catch (err) {
      console.error("SET DEFAULT WORK TYPE ERROR:", err);
      setError(err.message || "Failed to set the default work type.");
    }
  };

  const handleRestore = async (item) => {
    setError("");
    try {
      await restoreWorkType(item.work_type_id, supabase, actor);
      await afterChange();
    } catch (err) {
      console.error("RESTORE WORK TYPE ERROR:", err);
      setError(err.message || "Failed to restore work type.");
    }
  };

  const handleMove = async (item, direction) => {
    setError("");
    const reordered = reorderWorkTypeDisplayOrders(
      items,
      item.work_type_id,
      direction,
    );
    if (!reordered) {
      return;
    }

    const changed = reordered.filter((row) => {
      const previous = items.find(
        (candidate) => candidate.work_type_id === row.work_type_id,
      );
      return Number(previous?.display_order) !== row.display_order;
    });

    try {
      await Promise.all(
        changed.map((row) =>
          updateWorkType(
            row.work_type_id,
            { display_order: row.display_order },
            supabase,
            actor,
          ),
        ),
      );
      await afterChange();
    } catch (err) {
      console.error("REORDER WORK TYPE ERROR:", err);
      setError(err.message || "Failed to reorder work types.");
    }
  };

  const activeItems = items.filter((item) => item.is_active);
  const archivedItems = items.filter((item) => !item.is_active);

  if (isDesktop) {
    const groups = [
      { key: "active", header: "Active", rows: activeItems },
      ...(archivedItems.length > 0
        ? [{ key: "archived", header: "Archived", rows: archivedItems }]
        : []),
    ];
    return (
      <CrewPilotModuleShell
        title="Work Types"
        subtitle="Work Types employees choose when they submit a session"
        sectionLabel="Work & Time"
        onBack={onBack}
        layout={PAGE_LAYOUT.COMMAND}
      >
        <p className="adm-note">
          Active work types appear on Work Summary, in this order. The default
          is preselected for every new session. Archived work types stay on
          past time logs and are hidden from new sessions.
        </p>
        <div className="adm-main-side">
          {loading ? (
            <p className="adm-note">Loading…</p>
          ) : items.length === 0 ? (
            <DeskEmpty title="No active work types." />
          ) : (
            <DeskTable
              caption="Work types"
              groups={groups}
              rowKey={(item) => item.work_type_id}
              rowClassName={(item) => (item.is_active ? "" : "is-muted")}
              columns={[
                {
                  key: "order",
                  label: "Order",
                  width: "104px",
                  render: (item) => (
                    <AdmMoveButtons
                      name={item.name}
                      disableUp={
                        reorderWorkTypeDisplayOrders(items, item.work_type_id, -1) == null
                      }
                      disableDown={
                        reorderWorkTypeDisplayOrders(items, item.work_type_id, 1) == null
                      }
                      onUp={() => handleMove(item, -1)}
                      onDown={() => handleMove(item, 1)}
                    />
                  ),
                },
                {
                  key: "name",
                  label: "Work type",
                  render: (item) =>
                    editingId === item.work_type_id ? (
                      <div className="adm-edit">
                        <CPInput
                          className="cp-input-group--compact"
                          aria-label={`Rename ${item.name}`}
                          value={draftName}
                          onChange={setDraftName}
                        />
                      </div>
                    ) : (
                      <span className="dt-strong">{item.name}</span>
                    ),
                },
                {
                  key: "status",
                  label: "Status",
                  width: "170px",
                  render: (item) => (
                    <span className="adm-rowact">
                      <DeskPill tone={item.is_active ? "green" : "muted"}>
                        {item.is_active ? "Active" : "Archived"}
                      </DeskPill>
                      {item.is_default ? <DeskPill tone="gold">Default</DeskPill> : null}
                    </span>
                  ),
                },
                {
                  key: "actions",
                  label: "Actions",
                  align: "right",
                  width: "340px",
                  render: (item) =>
                    editingId === item.work_type_id ? (
                      <span className="adm-rowact">
                        <button
                          type="button"
                          className="kpi-back-btn adm-sm adm-primary"
                          onClick={async () => {
                            const ok = await handleRename(item, draftName);
                            if (ok) setEditingId(null);
                          }}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="kpi-back-btn adm-sm"
                          onClick={() => {
                            setDraftName(item.name);
                            setEditingId(null);
                          }}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <span className="adm-rowact">
                        <button
                          type="button"
                          className="kpi-back-btn adm-sm"
                          onClick={() => {
                            setDraftName(item.name);
                            setEditingId(item.work_type_id);
                          }}
                        >
                          Rename
                        </button>
                        {item.is_active && !item.is_default ? (
                          <button
                            type="button"
                            className="kpi-back-btn adm-sm"
                            onClick={() => handleSetDefault(item)}
                          >
                            Set as default
                          </button>
                        ) : null}
                        {item.is_active ? (
                          <button
                            type="button"
                            className="kpi-back-btn adm-sm adm-soft"
                            aria-disabled={item.is_default ? "true" : undefined}
                            title={
                              item.is_default ? ARCHIVE_DEFAULT_BLOCKED_MESSAGE : undefined
                            }
                            onClick={() => handleArchive(item)}
                          >
                            Archive
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="kpi-back-btn adm-sm"
                            onClick={() => handleRestore(item)}
                          >
                            Restore
                          </button>
                        )}
                      </span>
                    ),
                },
              ]}
            />
          )}

          <div className="rb-card">
            <div className="section-heading">ADD WORK TYPE</div>
            <CPInput
              label="Name"
              value={newName}
              onChange={setNewName}
              placeholder="New work type"
            />
            <div className="adm-actions adm-actions--plain">
              <button type="button" className="kpi-back-btn adm-primary" onClick={handleAdd}>
                Add Work Type
              </button>
            </div>
            {error ? (
              <p className="admin-placeholder-note" style={{ marginTop: "12px" }}>
                {error}
              </p>
            ) : null}
            {saved ? (
              <p className="admin-save-confirmation" role="status" style={{ marginTop: "12px" }}>
                Work types updated.
              </p>
            ) : null}
          </div>
        </div>
      </CrewPilotModuleShell>
    );
  }

  return (
    <CrewPilotModuleShell
      title="Work Types"
      subtitle="Work Types employees choose when they submit a session"
      sectionLabel="Work & Time"
      onBack={onBack}
      layout={PAGE_LAYOUT.FORM}
    >
      <div className="rb-card work-types-panel">
        <p className="admin-placeholder-note">
          Active work types appear on Work Summary, in this order. The default
          is preselected for every new session. Archived work types stay on
          past time logs and are hidden from new sessions.
        </p>

        <div className="work-types-add">
          <CPInput
            label="Name"
            value={newName}
            onChange={setNewName}
            placeholder="New work type"
          />
          <button type="button" className="kpi-back-btn" onClick={handleAdd}>
            Add Work Type
          </button>
        </div>

        {error ? (
          <p className="admin-placeholder-note" style={{ marginTop: "12px" }}>
            {error}
          </p>
        ) : null}

        {saved ? (
          <p className="admin-save-confirmation" role="status">
            Work types updated.
          </p>
        ) : null}

        {loading ? (
          <p className="admin-placeholder-note">Loading…</p>
        ) : (
          <>
            <div className="admin-data-label" style={{ marginTop: "16px" }}>
              Active
            </div>
            {activeItems.length === 0 ? (
              <p className="admin-placeholder-note">No active work types.</p>
            ) : (
              activeItems.map((item) => (
                <WorkTypeRow
                  key={item.work_type_id}
                  item={item}
                  items={items}
                  onRename={handleRename}
                  onArchive={handleArchive}
                  onRestore={handleRestore}
                  onMove={handleMove}
                  onSetDefault={handleSetDefault}
                />
              ))
            )}

            {archivedItems.length > 0 ? (
              <>
                <div className="admin-data-label" style={{ marginTop: "16px" }}>
                  Archived
                </div>
                {archivedItems.map((item) => (
                  <WorkTypeRow
                    key={item.work_type_id}
                    item={item}
                    items={items}
                    onRename={handleRename}
                    onArchive={handleArchive}
                    onRestore={handleRestore}
                    onMove={handleMove}
                    onSetDefault={handleSetDefault}
                  />
                ))}
              </>
            ) : null}
          </>
        )}
      </div>
    </CrewPilotModuleShell>
  );
}
